import {
  BanniereCadrage,
  CadrageImage,
  OperationCommerciale,
  RotationCadrage,
} from 'api-types/ffconnect';

// Cadrage des images de bannière des opérations commerciales.

// Ratio largeur / hauteur, par image : celui du rectangle de cadrage, verrouillé dans la modale du
// backoffice, et celui du bandeau client qui affiche l'image. Ce sont les ratios des deux aperçus
// de la maquette (808 × 249 et 461 × 249). Le bandeau ayant le ratio du rectangle, le client voit
// exactement le rectangle dessiné, sur tous les écrans.
export const RATIO_CADRAGE: Record<keyof BanniereCadrage, number> = {
  ordinateur: 808 / 249,
  mobile: 461 / 249,
};

/**
 * Ajoute à l'URL Cloudimage d'une image sa rotation par quart de tour, dans le sens antihoraire
 * (paramètre `r`). Sert seule pour afficher l'image entière tournée ; sans rotation, l'URL ressort inchangée.
 */
export function imageTournee(url: string, rotation: RotationCadrage): string {
  if (!url || rotation === 0) {
    return url;
  }
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}r=${rotation}`;
}

/**
 * Ajoute à l'URL Cloudimage d'une image son cadrage : le rectangle (`tl_px`, `br_px`), puis la
 * rotation. Le rectangle est en pixels du fichier d'origine, dans son repère avant rotation, parce
 * que Cloudimage découpe avant de tourner. Sans cadrage, l'URL ressort inchangée.
 */
export function imageCadree(
  url: string,
  cadrage: CadrageImage | undefined,
): string {
  if (!url || !cadrage) {
    return url;
  }
  const {x, y, largeur, hauteur, rotation} = cadrage;
  const sep = url.includes('?') ? '&' : '?';
  // `br_px` est le coin bas-droit du rectangle, pas sa taille.
  const url_rectangle = `${url}${sep}tl_px=${x}%2C${y}&br_px=${x + largeur}%2C${y + hauteur}`;
  return imageTournee(url_rectangle, rotation);
}

/**
 * Construit l'URL de l'image à afficher dans le bandeau d'une opération commerciale, pour un format
 * (`ordinateur` ou `mobile`) choisi par l'appelant. Elle ne fait qu'assembler l'adresse : c'est le
 * navigateur qui la demandera ensuite à Cloudimage. Partagée par le web et l'application mobile.
 *
 * - En format `mobile`, s'il existe une image mobile : l'image mobile, avec le cadrage `mobile`.
 * - Dans tous les autres cas : l'image ordinateur, avec le cadrage `ordinateur`, y compris en format
 *   `mobile` sans image mobile. Un cadrage est en pixels de son propre fichier, il ne suit jamais
 *   l'autre image.
 * - Sans image ordinateur dans ces autres cas : `undefined`. L'image mobile ne remplace jamais
 *   l'image ordinateur.
 *
 * La largeur d'affichage s'ajoute après, par `imageOptimisee`. D'où `undefined` plutôt qu'une chaîne
 * vide : `imageOptimisee` transformerait une URL vide en image d'article générique.
 */
export function urlImageBandeau(
  format: keyof BanniereCadrage,
  banniere: Pick<
    OperationCommerciale,
    'banniere_url' | 'banniere_url_mobile' | 'banniere_cadrage'
  >,
): string | undefined {
  const {banniere_url, banniere_url_mobile, banniere_cadrage} = banniere;
  // Verification de quelle image utiliser en fonction du format.
  // Sans image mobile =>  l'image ordinateur. L'inverse n'existe pas : sans image ordinateur, le
  // format ordinateur n'a pas d'image.
  if (format === 'mobile' && banniere_url_mobile) {
    return imageCadree(banniere_url_mobile, banniere_cadrage?.mobile);
  }
  // Une image retirée au backoffice peut valoir `''` : test de vérité, pas `??`.
  if (!banniere_url) {
    return undefined;
  }
  // Le cadrage suit l'image affichée, pas l'écran : sans image mobile, c'est l'image ordinateur
  // qui s'affiche en mobile, avec son propre cadrage.
  return imageCadree(banniere_url, banniere_cadrage?.ordinateur);
}
